/* eslint-disable no-await-in-loop */

/* eslint-disable no-return-await */

/* eslint-disable class-methods-use-this */
import { bugsnag, createUUID } from "@miplatform/utils";
import { DEFAULT_API_HANDLER_CONFIG } from "./constants";

// ===== REQUEST QUEUE MANAGER =====
export class RequestQueueManager {
  constructor(config = {}) {
    this.config = { ...DEFAULT_API_HANDLER_CONFIG, ...config };
    this.currentRequests = new Map();
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.queueProcessor = null;
  }

  async processRequest(request, executor) {
    if (this.currentRequests.size >= this.config.maxConcurrentRequests) {
      return new Promise((resolve, reject) => {
        this.requestQueue.push({
          request,
          executor,
          resolve,
          reject,
          timestamp: Date.now(),
          id: createUUID(),
        });
        this.startQueueProcessing();
      });
    }

    return this.executeRequest(request, executor);
  }

  async executeRequest(request, executor) {
    if (!request.requestId) {
      request.requestId = createUUID();
    }

    // Check for collision and generate new ID if needed
    while (this.currentRequests.has(request.requestId)) {
      request.requestId = createUUID();
    }

    this.currentRequests.set(request.requestId, {
      request,
      startTime: Date.now(),
    });

    try {
      return await executor();
    } finally {
      this.currentRequests.delete(request.requestId);
      this.startQueueProcessing();
    }
  }

  startQueueProcessing() {
    if (this.isProcessingQueue) return;

    if (this.queueProcessor) {
      clearTimeout(this.queueProcessor);
    }

    this.queueProcessor = setTimeout(() => {
      this.processQueueBatch();
    }, this.config.delayBetweenRequests);
  }

  processQueueBatch() {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;
    this.queueProcessor = null;

    try {
      // Always clean stale requests first
      this.removeStaleRequests();

      // Process available slots
      const availableSlots = Math.max(
        0,
        this.config.maxConcurrentRequests - this.currentRequests.size
      );

      if (availableSlots > 0 && this.requestQueue.length > 0) {
        const requestsToProcess = this.requestQueue.splice(0, availableSlots);

        requestsToProcess.forEach(({ request, executor, resolve, reject }) => {
          this.executeRequest(request, executor).then(resolve).catch(reject);
        });
      }

      // Continue processing if queue has items
      if (this.requestQueue.length > 0) {
        this.isProcessingQueue = false;
        this.startQueueProcessing();
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  removeStaleRequests() {
    const now = Date.now();
    const staleRequests = [];

    this.requestQueue = this.requestQueue.filter((item) => {
      if (now - item.timestamp > this.config.requestTimeoutMs) {
        staleRequests.push(item);
        return false;
      }
      return true;
    });

    staleRequests.forEach(({ reject, id }) => {
      try {
        const errorMessage = `Request ${id} timed out in queue`;
        const errorObj = {
          id,
          message: errorMessage,
          timestamp: new Date().toISOString(),
          type: "REQUEST_QUEUE_TIMEOUT",
        };

        bugsnag.notify(
          new Error(
            `Request Queue Timeout: ${JSON.stringify(errorObj, null, 2)}`
          )
        );

        reject(new Error(errorMessage));
      } catch (error) {
        bugsnag.notify(
          new Error(`Failed to reject stale request: ${error.message}`)
        );
      }
    });
  }

  cleanup() {
    this.requestQueue.forEach(({ reject }) => {
      reject(new Error("RequestQueueManager is being cleaned up"));
    });
    this.requestQueue = [];
    this.currentRequests.clear();
    this.isProcessingQueue = false;

    if (this.queueProcessor) {
      clearTimeout(this.queueProcessor);
      this.queueProcessor = null;
    }
  }
}

export default RequestQueueManager;
